import { useEffect, useMemo, useState } from "preact/hooks"
import { Plus, Upload } from "lucide-react"
import { Button } from "../../../../components/ui/button"
import { toast } from "../../../../components/ui/use-toast"
import { classes, useAsyncEffect } from "../../../../utils"
import { api_fetch } from "../../../../api/client"
import { UserState } from "@/auth/user"

// Components
import { FileUploadArea } from './file_upload'
import { CreateCalendarForm } from './calendar_forms'
import { DiscountSection, PlanningAssumptionsSection, useProductDetails } from './promotion_forms'
import { PromotionFormField, SearchableSelectField } from "./form_fields"
import { ProductMultiSelect } from "./product_multi_select"
import { PromotionAccountFields } from "./promotion_account_fields"
import { OwnershipSection } from "./ownership_section"
import { PromotionLinesEditor } from "./promotion_lines_editor"
import { PromotionSummaryPanel, SummaryMissingField } from "./promotion_summary_panel"

// Hooks
import { CopyForwardOptions, usePromotionalContextAPI } from '../hooks/use_promotional_context_api'
import { use_org_users, user_name_by_id } from "../hooks/use_org_users"
import { CalendarInfo, Promotion, PromotionStage } from "../types"
import { createPromotionTypeOptions } from "../constants"
import { has_unit_deal, planned_amount_help, promotion_type_layout, start_date_help, strip_fields_for_type } from "../promotion_type_layout"
import { promotion_estimate } from "../planning_assumptions"
import { margin_impact, suggested_unit_cost, unit_discount } from "../deal_math"
import { validatePromotion } from "../validation"
import { find_potential_duplicate_promotions } from "../duplicate_check"
import { promotion_fields_from_calendar } from "../calendar_prefill"
import { CopyForwardPromotionDialog } from "../../promo_calendar/components/copy_forward_promotion_dialog"
import { SearchableSelect, SearchableSelectOption } from "../../../../components/searchable_select"
import { PromotionLastRunNotice } from "./last_run_notice"

interface PromotionalContextProps {
  className?: string
  calendars: CalendarInfo[]
  onCalendarCreated?: (calendarId: string) => void
  onCreatePromotion: (calendarId: string, promotion: Promotion, files: File[]) => Promise<Promotion | null>
  onCopyForward: (options: CopyForwardOptions) => Promise<Promotion[] | null>
  /** Called with the created promotions after a successful copy-forward (e.g. to open the first one). */
  onPromotionsCopied?: (promotions: Promotion[]) => void
  /** Ensures every calendar's promotions are hydrated (copy-forward searches across all years). */
  onEnsureAllPromotionsLoaded?: () => void
  /** True while calendars/promotions are still hydrating (surfaces a loading state in copy-forward). */
  promotionsLoading?: boolean
}

type WorkspaceAction = "create" | "upload" | "calendar"

/** Sentinel option value in the planning-calendar select that jumps to the create-calendar form. */
const ADD_NEW_CALENDAR_OPTION = "__add_new_calendar__"

interface PromotionCreationWizardProps {
  calendars: CalendarInfo[]
  /** True while the calendar list itself is still loading (shows a stable loading state). */
  calendarsLoading?: boolean
  onCreatePromotion: (calendarId: string, promotion: Promotion, files: File[]) => Promise<Promotion | null>
  /** Jump to the create-calendar form (used by the "+ Add new calendar" option). */
  onAddNewCalendar: () => void
  /** Called with the created promotion (e.g. to navigate to it). When omitted the form just resets. */
  onCreated?: (promotion: Promotion) => void
  /** Select this calendar (and copy its fields) once it appears in `calendars` — e.g. right after creating it. */
  autoSelectCalendarId?: string | null
  /** Start from a copy of this promotion (the Duplicate button on a promotion). */
  initialPromotion?: Promotion | null
  className?: string
}

const EMPTY_PROMOTION: Promotion = {
  name: "",
  promotion_type: "",
  products: [],
  product_ids: [],
  customer: "",
  retailer: "",
  brand: "",
  start_date: "",
  end_date: "",
  stage: "Pending",
}

/**
 * PURE: a promotion as the form starts when duplicating `source`: the same deal, products, lines
 * and account, but its own identity — no id or promo number, a fresh draft owned by whoever is
 * duplicating, with no approval carried over, and named as a copy until renamed.
 */
export function promotion_copy_for_form(source: Promotion, owner_id: string | undefined): Promotion {
  return {
    ...source,
    id: undefined,
    remote_promo_id: undefined,
    name: /\bcopy\b/i.test(source.name) ? source.name : `${source.name} (copy)`,
    stage: "Pending",
    owner_id: owner_id ?? source.owner_id,
    approver_id: undefined,
    copied_from_promotion_id: source.id,
    file_count: undefined,
    period_allocations: undefined,
    latest_estimate_amount: undefined,
    lines: (source.lines ?? []).map((line) => ({ ...line, id: undefined })),
  }
}

/**
 * PURE: what is still needed before the promotion can be saved in `stage`, with the field to
 * scroll to for each. Submitting needs an approver; a draft does not.
 */
export function missing_requirements(
  promotion: Promotion,
  calendar_id: string,
  stage: PromotionStage,
): SummaryMissingField[] {
  const layout = promotion_type_layout(promotion.promotion_type)
  const missing: SummaryMissingField[] = []
  if (!calendar_id) missing.push({ label: "a planning calendar", anchor: "promotion-calendar" })
  if (!promotion.name.trim()) missing.push({ label: "a name", anchor: "promotion-name" })
  if (!promotion.promotion_type) missing.push({ label: "a promotion type", anchor: "promotion-type" })
  if (!promotion.customer) missing.push({ label: "a customer", anchor: "promotion-customer" })
  if (promotion.products.length === 0 && !layout.products_optional) missing.push({ label: "at least one product", anchor: "promotion-products" })
  if (stage === "Submitted" && !promotion.approver_id) missing.push({ label: "an approver", anchor: "promotion-approver" })
  return missing
}

export function PromotionCreationWizard({
  calendars,
  calendarsLoading,
  onCreatePromotion,
  onAddNewCalendar,
  onCreated,
  autoSelectCalendarId,
  initialPromotion,
  className,
}: PromotionCreationWizardProps) {
  const current_user = UserState.use()
  const org_users = use_org_users()
  // Staff switched into a brand are not on its user list but can still be named as owner.
  const users = current_user ? [current_user, ...(org_users ?? [])] : org_users
  const [calendarId, setCalendarId] = useState("")
  const [files, setFiles] = useState<File[]>([])
  const [isSaving, setIsSaving] = useState(false)
  const [promotion, setPromotion] = useState<Promotion>(EMPTY_PROMOTION)
  const [attempted, setAttempted] = useState(false)
  const [showLines, setShowLines] = useState(false)
  const [unit_cost, setUnitCost] = useState<number | undefined>(undefined)
  const [products, remember_product] = useProductDetails(promotion.product_ids)
  const [promotionTypes, setPromotionTypes] = useState<SearchableSelectOption[]>(
    createPromotionTypeOptions([]),
  )

  // Load the org's actual promotion types (same source as the edit forms) so the wizard doesn't
  // invent new type spellings; falls back to the defaults on error.
  useAsyncEffect(async () => {
    const result = await api_fetch<string[]>("/promotion-types")
    if (result.ok) {
      setPromotionTypes(createPromotionTypeOptions(result.value.data))
    }
  }, [])

  // The rep creating the plan owns it until they pick someone else.
  useEffect(() => {
    if (current_user?.id && !promotion.owner_id) setPromotion((previous) => ({ ...previous, owner_id: previous.owner_id || current_user.id }))
  }, [current_user?.id])

  // Duplicating: start from the source promotion, in its calendar once that has hydrated.
  useEffect(() => {
    if (!initialPromotion) return
    setPromotion(promotion_copy_for_form(initialPromotion, current_user?.id))
    setShowLines((initialPromotion.lines ?? []).length > 0)
  }, [initialPromotion?.id])
  useEffect(() => {
    if (!initialPromotion?.id || calendarId) return
    const home = calendars.find((calendar) => calendar.promotions.some((candidate) => candidate.id === initialPromotion.id))
    if (home) setCalendarId(home.id)
  }, [initialPromotion?.id, calendars])

  // What the form asks for follows the promotion type (see promotion_type_layout).
  const layout = promotion_type_layout(promotion.promotion_type)

  // Non-blocking duplicate warning: same customer + similar name, or same customer + same type +
  // overlapping dates. Uses whatever promotions have hydrated so far.
  const existingPromotions = useMemo(() => calendars.flatMap(calendar => calendar.promotions), [calendars])
  const potentialDuplicates = useMemo(() => {
    if (!promotion.customer || promotion.name.trim().length < 3) return []
    return find_potential_duplicate_promotions(
      {
        name: promotion.name,
        customer: promotion.customer,
        promotion_type: promotion.promotion_type,
        start_date: promotion.start_date,
        end_date: promotion.end_date,
      },
      existingPromotions,
    ).filter((duplicate) => duplicate.id !== initialPromotion?.id)
  }, [existingPromotions, promotion.name, promotion.customer, promotion.promotion_type, promotion.start_date, promotion.end_date])

  function handleCalendarChange(nextCalendarId: string) {
    if (nextCalendarId === ADD_NEW_CALENDAR_OPTION) {
      onAddNewCalendar()
      return
    }
    setCalendarId(nextCalendarId)
    // Copy customer/retailer/brand/year forward from the selected calendar.
    const calendar = calendars.find(row => row.id === nextCalendarId)
    setPromotion(previous => ({ ...previous, ...promotion_fields_from_calendar(calendar) }))
  }

  // A calendar created via "+ Add new calendar" becomes the selection once it loads.
  useEffect(() => {
    if (!autoSelectCalendarId || autoSelectCalendarId === calendarId) return
    if (calendars.some(row => row.id === autoSelectCalendarId)) {
      handleCalendarChange(autoSelectCalendarId)
    }
  }, [autoSelectCalendarId, calendars])

  // Promotions hydrate lazily, so the selected calendar's customer/retailer may only be known
  // after selection. Fill fields that are still blank when the data arrives — never overwrite
  // anything the user already set.
  useEffect(() => {
    if (!calendarId) return
    const fields = promotion_fields_from_calendar(calendars.find(row => row.id === calendarId))
    setPromotion(previous => ({
      ...previous,
      customer: previous.customer || fields.customer || "",
      retailer: previous.retailer || fields.retailer || "",
      brand: previous.brand || fields.brand || "",
      fiscal_year: previous.fiscal_year ?? fields.fiscal_year,
    }))
  }, [calendars, calendarId])

  /** Save as a draft (Pending) or submit for approval (Submitted). */
  async function handleSave(stage: PromotionStage) {
    setAttempted(true)
    const missing = missing_requirements(promotion, calendarId, stage)
    if (missing.length > 0) {
      toast({ title: "Still needed", description: missing.map((field) => field.label).join(", "), variant: "destructive" })
      document.getElementById(missing[0].anchor)?.scrollIntoView({ behavior: "smooth", block: "center" })
      return
    }
    // Fields the chosen type does not use stay in the form while editing (switching type and back
    // loses nothing) but are not saved against it.
    const to_save = strip_fields_for_type({ ...promotion, stage })
    if (!validatePromotion(to_save)) return
    setIsSaving(true)
    try {
      const created = await onCreatePromotion(calendarId, to_save, files)
      if (created) {
        setFiles([])
        setPromotion({ ...EMPTY_PROMOTION, owner_id: current_user?.id })
        setAttempted(false)
        setShowLines(false)
        onCreated?.(created)
      }
    } finally {
      setIsSaving(false)
    }
  }

  const missing_for_draft = missing_requirements(promotion, calendarId, "Pending")
  const missing_for_submit = missing_requirements(promotion, calendarId, "Submitted")
  const highlight = (anchor: string) => attempted && missing_for_submit.some((field) => field.anchor === anchor)
  const selected_calendar = calendars.find((calendar) => calendar.id === calendarId)
  const estimate = promotion_estimate(promotion)
  const catalog_costs = (promotion.product_ids ?? []).map((id) => products[id]).filter(Boolean).map(suggested_unit_cost).filter((cost): cost is number => cost != null)
  const catalog_cost = catalog_costs.length > 0 && new Set(catalog_costs).size === 1 ? catalog_costs[0] : undefined
  const margin = margin_impact(promotion.regular_price, unit_cost ?? catalog_cost, unit_discount(promotion)?.amount)
  const has_products = (promotion.product_ids?.length ?? 0) > 0 || promotion.products.length > 0
  // Until a type is picked the form cannot know whether products are optional, so it asks for them.
  const products_optional = Boolean(promotion.promotion_type) && layout.products_optional

  return (
    <div class={classes("lg:grid lg:grid-cols-[minmax(0,1fr)_300px] lg:items-start lg:gap-6", className)}>
      <div class="rounded-lg border border-gray-200 bg-white p-4">
        <h3 class="text-lg font-medium text-gray-900">{initialPromotion ? "Duplicate promotion" : "Create Promotion"}</h3>
        <p class="mt-1 text-sm text-gray-600">
          {initialPromotion
            ? `Starting from "${initialPromotion.name}". Change what differs, then save it as a draft or submit it.`
            : "Build a promotion with details, timing, planned spend, and supporting files. Save it as a draft, or submit it for approval."}
        </p>
        <div class="mt-4 space-y-4">
          <div id="promotion-calendar">
            <label class={`block text-sm font-medium mb-1 ${highlight("promotion-calendar") ? "text-red-700" : "text-gray-700"}`}>
              Planning Calendar <span class="text-red-500">*</span>
            </label>
            <SearchableSelect
              value={calendarId}
              onValueChange={handleCalendarChange}
              options={[
                ...calendars.map(calendar => ({ value: calendar.id, label: calendar.calendar_name })),
                { value: ADD_NEW_CALENDAR_OPTION, label: "+ Add new calendar" },
              ]}
              placeholder={calendarsLoading ? "Loading calendars..." : "Select a planning calendar"}
              searchPlaceholder="Search planning calendars..."
              emptyMessage={calendarsLoading ? "Loading calendars..." : "No matching calendars."}
              triggerClassName={highlight("promotion-calendar") ? "border-red-400 bg-red-50" : undefined}
            />
            <p class="mt-1 text-xs text-gray-500">
              The calendar groups promotions by customer and year — customer, retailer, and year copy forward from it.
            </p>
          </div>
          <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
            <PromotionFormField
              label="Promotion Name"
              value={promotion.name}
              onChange={(value) => setPromotion({ ...promotion, name: value as string })}
              placeholder="March display program"
              help="Use the retailer's program name if it has one — deductions reference it."
              required
              error={highlight("promotion-name")}
              id="promotion-name"
            />
            <SearchableSelectField
              label="Promotion Type"
              value={promotion.promotion_type || ""}
              // Changing type keeps what was entered: fields the new type does not use are hidden, not wiped.
              onChange={(value) => setPromotion({ ...promotion, promotion_type: value })}
              options={promotionTypes}
              placeholder="Select promotion type"
              searchPlaceholder="Search promotion types..."
              emptyMessage="No matching promotion types."
              required
              error={highlight("promotion-type")}
              id="promotion-type"
            />
            <PromotionAccountFields
              customer={promotion.customer || ""}
              retailer={promotion.retailer || ""}
              onCustomerChange={(value) => setPromotion({ ...promotion, customer: value })}
              onRetailerChange={(value) => setPromotion({ ...promotion, retailer: value })}
              calendars={calendars}
              customerError={highlight("promotion-customer")}
            />
            <PromotionFormField
              label="Planning Group"
              value={promotion.planning_group || ""}
              onChange={(value) => setPromotion({ ...promotion, planning_group: value as string })}
              placeholder="e.g., Natural channel"
              help="Optional label for grouping promotions your own way (a region, channel or segment). It shows on the promo calendar's cards and can be searched there and in Copy promotions forward. It does not affect spend or deduction matching."
            />
            <PromotionFormField
              label="Fiscal Year"
              value={promotion.fiscal_year}
              onChange={(value) => setPromotion({ ...promotion, fiscal_year: value as number | undefined })}
              type="number"
              placeholder="e.g., 2026"
              help="Filled in from the planning calendar's year, not from the dates — change it if your fiscal year differs. Used to find this promotion by year, e.g. when copying a year forward."
            />
            <PromotionFormField
              label="Start Date"
              value={promotion.start_date || ""}
              onChange={(value) => setPromotion({ ...promotion, start_date: value as string })}
              type="date"
              help={start_date_help(layout)}
            />
            <PromotionFormField
              label="End Date"
              value={promotion.end_date || ""}
              onChange={(value) => setPromotion({ ...promotion, end_date: value as string })}
              type="date"
              error={Boolean(promotion.start_date && promotion.end_date && promotion.end_date < promotion.start_date)}
              help={promotion.start_date && promotion.end_date && promotion.end_date < promotion.start_date ? "The end date is before the start date." : undefined}
            />
          </div>

          <ProductMultiSelect
            label="Products"
            selectedIds={promotion.product_ids || []}
            selectedLabels={promotion.products}
            onChange={(productIds, productLabels) => setPromotion({ ...promotion, product_ids: productIds, products: productLabels })}
            onProductPicked={remember_product}
            required={!products_optional}
            allowAllProducts={products_optional}
            help={products_optional ? "Optional for fees and accruals — leave it empty or pick All products when the deal is not tied to specific items." : undefined}
            error={highlight("promotion-products")}
            id="promotion-products"
          />

          {/* Deal terms (per unit) vs total budget — the two money fields people mix up. */}
          {promotion.promotion_type ? (
            <DiscountSection promotion={promotion} onChange={setPromotion} layout={layout} products={products} />
          ) : (
            <p class="rounded-md border border-dashed border-gray-300 p-3 text-xs text-gray-500">
              Pick a promotion type and the deal fields for it appear here — a discount for price and scan
              deals, only Planned Amount for fees, slotting and accruals.
            </p>
          )}

          {/* One row per product, for a promo over several items at different prices or volumes. */}
          {promotion.promotion_type && has_unit_deal(layout) && has_products ? (
            <div class="space-y-2">
              <button type="button" onClick={() => setShowLines(!showLines)} class="text-sm font-medium text-blue-600 hover:text-blue-800">
                {showLines ? "Hide" : "Set"} discount and volume per product ({Math.max(promotion.product_ids?.length ?? 0, promotion.products.length)})
              </button>
              {showLines && <PromotionLinesEditor promotion={promotion} onChange={setPromotion} products={products} />}
            </div>
          ) : null}

          <PromotionFormField
            label="Planned Amount"
            value={promotion.planned_amount}
            onChange={(value) => setPromotion({ ...promotion, planned_amount: value as number | undefined })}
            type="number"
            step="0.01"
            prefix="$"
            placeholder="Total expected spend"
            help={promotion.promotion_type ? planned_amount_help(layout) : "Total expected spend for the whole promotion."}
            id="promotion-planned-amount"
          />
          <PromotionLastRunNotice promotion={promotion} />

          {/* Volume, expected lift, and store participation — the assumptions behind the planned amount.
              Only for volume-driven deals; a flat fee, slotting or accrual has none. */}
          {promotion.promotion_type && layout.show_volume && (
            <PlanningAssumptionsSection promotion={promotion} onChange={setPromotion} showVolume products={products} unit_cost={unit_cost} onUnitCostChange={setUnitCost} />
          )}

          <OwnershipSection
            promotion={promotion}
            onChange={(changes) => setPromotion({ ...promotion, ...changes })}
            approverError={highlight("promotion-approver")}
          />

          <div>
            <span class="block text-sm font-medium text-gray-700">Files</span>
            <div class="mt-1 flex flex-wrap items-center gap-2">
              <label class="inline-flex cursor-pointer items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
                <Upload className="h-4 w-4" />
                {files.length > 0 ? "Choose different files" : "Choose files"}
                <input
                  class="hidden"
                  type="file"
                  multiple
                  onChange={(event) => setFiles(Array.from((event.target as HTMLInputElement).files || []))}
                />
              </label>
              {files.map((file) => (
                <span key={file.name} class="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
                  {file.name}
                </span>
              ))}
            </div>
            <p class="mt-1 text-xs text-gray-500">
              Optional — attach the deal sheet or retailer confirmation so validation has backup later.
            </p>
          </div>

          {potentialDuplicates.length > 0 && (
            <div class="rounded-md border border-amber-300 bg-amber-50 p-3">
              <p class="text-sm font-medium text-amber-800">
                Possible duplicate{potentialDuplicates.length === 1 ? "" : "s"} — a similar promotion already exists for this customer:
              </p>
              <ul class="mt-1 space-y-0.5">
                {potentialDuplicates.map(duplicate => (
                  <li key={duplicate.id || duplicate.name} class="text-xs text-amber-700">
                    {duplicate.id ? (
                      <a class="underline hover:text-amber-900" href={`/trade-promotion-management?promotion=${duplicate.id}`} target="_blank" rel="noopener noreferrer">
                        {duplicate.name}
                      </a>
                    ) : (
                      duplicate.name
                    )}
                    {duplicate.remote_promo_id ? ` (${duplicate.remote_promo_id})` : ""}
                    {duplicate.start_date ? ` · ${duplicate.start_date}${duplicate.end_date ? ` to ${duplicate.end_date}` : ""}` : ""}
                  </li>
                ))}
              </ul>
              <p class="mt-1 text-xs text-amber-700">You can still create this promotion if it's genuinely new.</p>
            </div>
          )}

          <div class="flex flex-wrap items-center gap-3 border-t pt-4">
            <Button onClick={() => handleSave("Pending")} variant="outline" disabled={isSaving}>
              {isSaving ? "Saving..." : "Save as draft"}
            </Button>
            <Button onClick={() => handleSave("Submitted")} disabled={isSaving}>
              {isSaving ? "Saving..." : "Submit for approval"}
            </Button>
            {missing_for_draft.length > 0 ? (
              <span class={`text-xs ${attempted ? "text-red-700" : "text-gray-500"}`}>Still needed: {missing_for_draft.map((field) => field.label).join(", ")}.</span>
            ) : missing_for_submit.length > 0 ? (
              <span class="text-xs text-gray-500">Ready to save as a draft. To submit, pick an approver.</span>
            ) : (
              <span class="text-xs text-gray-500">A draft stays with you; submitting sends it to the approver.</span>
            )}
          </div>
        </div>
      </div>

      <PromotionSummaryPanel
        className="mt-4 lg:mt-0 lg:sticky lg:top-4"
        promotion={promotion}
        calendar_name={selected_calendar?.calendar_name}
        estimate={estimate}
        margin={margin}
        owner_name={user_name_by_id(users, promotion.owner_id)}
        approver_name={user_name_by_id(users, promotion.approver_id)}
        missing={missing_for_submit}
        attempted={attempted}
        file_count={files.length}
      />
    </div>
  )
}

export function PromoCreation({
  className,
  calendars,
  onCalendarCreated,
  onCreatePromotion,
  onCopyForward,
  onPromotionsCopied,
  onEnsureAllPromotionsLoaded,
  promotionsLoading,
}: PromotionalContextProps) {
  // API hook with creation logic
  const {
    isUploading,
    isCreatingCalendar,
    handleFileUpload,
    handleCreateCalendar,
  } = usePromotionalContextAPI()

  // UI state
  const [activeAction, setActiveAction] = useState<WorkspaceAction>("create")
  const [copyForwardOpen, setCopyForwardOpen] = useState(false)

  // Enhanced handlers that manage UI state
  async function onFileUpload(files: File[]) {
    const newCalendarId = await handleFileUpload(files)
    if (newCalendarId && onCalendarCreated) {
      onCalendarCreated(newCalendarId)
    }
  }

  async function onCreateCalendar(calendarData: any) {
    const newCalendarId = await handleCreateCalendar(calendarData)
    if (newCalendarId) {
      setActiveAction("create")
      if (typeof newCalendarId === 'string' && onCalendarCreated) {
        onCalendarCreated(newCalendarId)
      }
    }
  }

  return (
    <div class={classes("", className)}>
      <div class="mb-4 flex flex-wrap justify-center gap-2">
        <Button onClick={() => setActiveAction("create")} variant={activeAction === "create" ? "default" : "outline"}>
          Create Promotion
        </Button>
        <Button onClick={() => setActiveAction("calendar")} variant={activeAction === "calendar" ? "default" : "outline"}>
          <Plus className="h-4 w-4 mr-2" />
          New Calendar
        </Button>
        <Button onClick={() => setCopyForwardOpen(true)} variant="outline">
          Copy Existing Promotions Forward
        </Button>
        <Button onClick={() => setActiveAction("upload")} variant={activeAction === "upload" ? "default" : "outline"}>
          <Upload className="h-4 w-4 mr-2" />
          Upload Calendar
        </Button>
      </div>
      <CopyForwardPromotionDialog
        open={copyForwardOpen}
        onOpenChange={setCopyForwardOpen}
        calendars={calendars}
        onCopyForward={onCopyForward}
        onCopied={onPromotionsCopied}
        onEnsureAllPromotionsLoaded={onEnsureAllPromotionsLoaded}
        promotionsLoading={promotionsLoading}
      />

      {/* Upload and Calendar Creation Section */}
      <div id="upload-section" class="space-y-6">
        {activeAction === "upload" && (
          <div class="my-6">
            <p class="text-sm text-gray-600 pt-2">
              Upload your promotional calendar files so that the AI Wizard can accurately validate your retailer promotion deductions.
            </p>
            <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-3 text-xs text-blue-700">
              Please review the imported promotion results carefully before using them for planning or deduction validation.
            </div>
          </div>
        )}

        {/* Hidden, never unmounted, while another panel is open: unmounting threw away everything
            typed, so cancelling "Create a calendar" reset the form. */}
        <div class={activeAction === "create" ? undefined : "hidden"}>
          <PromotionCreationWizard
            calendars={calendars}
            onCreatePromotion={onCreatePromotion}
            onAddNewCalendar={() => setActiveAction("calendar")}
          />
        </div>

        {activeAction === "upload" && (
          <FileUploadArea onUpload={onFileUpload} isUploading={isUploading} />
        )}

        {/* Manual Calendar Creation */}
        {activeAction === "calendar" && (
          <div class="space-y-4 pb-12">
          <div class="flex justify-center">
            <Button
              onClick={() => setActiveAction("create")}
              variant="outline"
              disabled={isCreatingCalendar}
            >
              <Plus className="h-4 w-4 mr-2" />
              Back to Promotion Creation
            </Button>
          </div>

          <CreateCalendarForm
            onSave={onCreateCalendar}
            onCancel={() => setActiveAction("create")}
            isSaving={isCreatingCalendar}
          />
          </div>
        )}
      </div>
    </div>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("promotion_copy_for_form", () => {
    it("keeps the deal but gives the copy its own identity as a fresh draft", () => {
      const source: Promotion = {
        id: "p1",
        remote_promo_id: "FP-00012",
        name: "March TPR",
        products: ["Level-1 Bar"],
        product_ids: ["prod-1"],
        promotion_type: "TPR",
        discount_amount: 1.5,
        regular_price: 3.99,
        stage: "Approved",
        owner_id: "u1",
        approver_id: "u2",
        file_count: 3,
        lines: [{ id: "l1", product_id: "prod-1", discount_amount: 1.5 }],
      }
      const copy = promotion_copy_for_form(source, "u3")
      expect(copy).toMatchObject({
        id: undefined,
        remote_promo_id: undefined,
        name: "March TPR (copy)",
        stage: "Pending",
        owner_id: "u3",
        approver_id: undefined,
        copied_from_promotion_id: "p1",
        discount_amount: 1.5,
        regular_price: 3.99,
        product_ids: ["prod-1"],
      })
      expect(copy.lines).toEqual([{ id: undefined, product_id: "prod-1", discount_amount: 1.5 }])
      expect(promotion_copy_for_form({ ...source, name: "March TPR copy" }, undefined).name).toBe("March TPR copy")
    })
  })

  describe("missing_requirements", () => {
    const draft: Promotion = { name: "x", products: ["Bar"], promotion_type: "TPR", customer: "KeHE" }
    it("lists what a draft still needs, with the field to go to", () => {
      expect(missing_requirements({ ...draft, name: "", customer: "" }, "", "Pending").map((field) => field.anchor)).toEqual([
        "promotion-calendar",
        "promotion-name",
        "promotion-customer",
      ])
      expect(missing_requirements(draft, "cal", "Pending")).toEqual([])
    })
    it("asks for an approver only to submit, and no products for a fee", () => {
      expect(missing_requirements(draft, "cal", "Submitted").map((field) => field.label)).toEqual(["an approver"])
      expect(missing_requirements({ ...draft, approver_id: "u2" }, "cal", "Submitted")).toEqual([])
      expect(missing_requirements({ ...draft, products: [], promotion_type: "AD Fee - Signage" }, "cal", "Pending")).toEqual([])
      expect(missing_requirements({ ...draft, products: [] }, "cal", "Pending").map((field) => field.anchor)).toEqual(["promotion-products"])
    })
  })
}
